import { decode } from 'html-entities';

import type { CartGetItem, MakeCartAPIArgs } from 'src/types';
import { baseFetch } from 'src/utils';

import { normalizePriceToNumber } from './priceUtils';

/** Loosely-typed item as returned by the various vendor search adapters. */
export interface SearchResultItem {
  asin?: string;
  ASIN?: string;
  itemid?: string;
  id?: string;
  title?: string;
  name?: string;
  image?: string;
  imageUrl?: string;
  img?: string;
  thumbnail?: string;
  price?: string | number;
  priceccy?: string;
  priceccyS?: string;
  ccy?: string;
  ccyS?: string;
  url?: string;
  sku?: string | number;
  skuString?: string;
  productId?: string;
  [key: string]: unknown;
}

/** Editor-side cart item. Superset of the fields the UI and save endpoint need. */
export interface MakeItem {
  asin: string;
  title?: string;
  image?: string;
  price?: string | number;
  quantity: number;
  url?: string;
  originalUrl?: string;
  sku?: string;
  skuString?: string;
  priceccy?: string;
  priceccyS?: string;
  ccy?: string;
  ccyS?: string;
  brandId?: string;
  uom?: string;
  offerId?: string;
  offerListingID?: string;
  productId?: string;
}

function str(v: unknown): string | undefined {
  if (v == null) return undefined;
  const s = String(v).trim();
  return s ? s : undefined;
}

export function searchResultToMakeItem(r: SearchResultItem): MakeItem {
  // Vendor APIs return titles with HTML entities (e.g. &#8482; for ™); decode
  // to plain text so React renders them literally and saved carts store the
  // clean string.
  const title = str(r.title) ?? str(r.name);
  return {
    asin: str(r.asin) ?? str(r.ASIN) ?? str(r.itemid) ?? str(r.id) ?? str(r.sku) ?? '',
    title: title && decode(title),
    image: str(r.image) ?? str(r.imageUrl) ?? str(r.img) ?? str(r.thumbnail),
    price: r.price,
    quantity: 1,
    url: str(r.url),
    sku: str(r.sku),
    skuString: str(r.skuString),
    priceccy: str(r.priceccy) ?? str(r.ccy),
    priceccyS: str(r.priceccyS) ?? str(r.ccyS),
    ccy: str(r.ccy),
    ccyS: str(r.ccyS),
    productId: str(r.productId),
  };
}

// Share-link path form used by /get rows and the Excel export.
export function toShareUrlPath(url?: string): string {
  if (!url) return '';
  return url.startsWith('/u/') || url.startsWith('/uv/') || url.startsWith('/uvv/')
    ? url
    : `/u/${encodeURIComponent(url)}`;
}

// MakeItem -> the CartGetItem shape the shared CartItem component renders.
export function makeItemToCartGetItem(item: MakeItem, locale: string): CartGetItem {
  return {
    asin: item.asin,
    title: item.title ?? '',
    image: item.image,
    quantity: item.quantity,
    price: normalizePriceToNumber(item.price, locale),
    url: toShareUrlPath(item.url),
    originalUrl: item.originalUrl ?? item.url ?? '',
    newVendorItemURL: '',
    sku: item.sku,
    skuString: item.skuString,
    ccyS: item.ccyS || item.priceccyS,
    priceccy: item.priceccy || item.ccy,
    savings: null,
    savingsBool: false,
    savingsPrice: '',
    brandId: item.brandId,
    uom: item.uom,
  };
}

export function cartGetItemToMakeItem(i: CartGetItem): MakeItem {
  return {
    asin: i.asin,
    title: i.title,
    image: i.image,
    price: i.price,
    quantity: i.quantity || 1,
    url: i.url,
    originalUrl: i.originalUrl,
    sku: i.sku,
    skuString: i.skuString,
    priceccy: i.priceccy,
    priceccyS: i.ccyS,
    ccy: i.priceccy,
    ccyS: i.ccyS,
    brandId: i.brandId,
    uom: i.uom,
  };
}

export interface SearchResponse {
  items: SearchResultItem[];
  redirectedTo?: string;
}

export async function searchItems(
  vendor: string,
  keywords: string,
  region?: string,
  zip?: string,
  shop?: string,
): Promise<SearchResponse> {
  const url = new URL(
    `/api/make/${encodeURIComponent(vendor)}/${encodeURIComponent(keywords)}`,
    window.location.origin,
  );
  if (region) url.searchParams.set('region', region);
  if (zip) url.searchParams.set('zip', zip);
  if (shop) url.searchParams.set('shop', shop);

  const res = await fetch(url.toString(), { credentials: 'include' });

  // Some vendors redirect (e.g. to install the extension) — surface it.
  if (res.redirected) return { items: [], redirectedTo: res.url };
  if (!res.ok) throw new Error(`Search failed (${res.status})`);

  const data = await res.json();
  const items: SearchResultItem[] = Array.isArray(data)
    ? data
    : Array.isArray(data?.items)
      ? data.items
      : [];
  return { items };
}

export interface SaveCartArgs {
  items: MakeItem[];
  vendor: string;
  cartId?: string | null;
  title?: string;
  note?: string;
  password?: string;
  dest?: string;
  ap?: string;
  ccy?: string;
}

export async function saveCart(args: SaveCartArgs): Promise<string> {
  const cart = args.items.map((i) => ({
    asin: i.asin,
    name: i.title || null,
    title: i.title || '',
    img: i.image || null,
    image: i.image,
    price: i.price ?? '',
    quantity: i.quantity,
    sku: i.sku,
    skuString: i.skuString,
    url: i.url || '',
    originalUrl: i.originalUrl || '',
    priceccy: i.priceccy,
    priceccyS: i.priceccyS,
    ccyS: i.ccyS,
    brandId: i.brandId,
    uom: i.uom,
    offerId: i.offerId,
    offerListingID: i.offerListingID,
    productId: i.productId,
  }));

  const body: MakeCartAPIArgs = {
    cartid: args.cartId || null,
    cart: cart as unknown as CartGetItem[],
    vendor: args.vendor,
    title: args.title || null,
    note: args.note || null,
    dest: args.dest || null,
    password: args.password || null,
    ap: args.ap || null,
    ccy: args.ccy || null,
  };

  const res = await baseFetch('/api/make/saveWithMetadata', {
    method: 'POST',
    body: JSON.stringify(body),
  });

  if (!res.ok) throw new Error('Failed to save cart');

  const data = await res.json();
  if (data.error) throw new Error(data.error);

  return data.cartid as string;
}
